import { AsyncPipe } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { Router, RouterOutlet } from "@angular/router";
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import {
  AccountInfo,
  AuthError,
  BrowserAuthError,
  BrowserAuthErrorMessage,
  BrowserUtils,
  EventError,
  EventType,
  InteractionStatus,
  Logger,
  LogLevel,
} from "@azure/msal-browser";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
} from "rxjs/operators";
import { UserRegistrationService } from "src/app/core/auth/user-registration.service";
import {
  firstExistentValueFrom,
  getEnumMember,
  isExistent,
  isNavigationStateChange,
} from "src/utils";
import { AlertsComponent } from "./core/alerts.component";
import { ApplicationInsightsService } from "./core/application-insights.service";
import { passwordResetAuthority, UserService } from "./core/auth";
import { UserRole } from "./core/b2c-users.service";
import { GlobalRequestPrefix } from "./core/constants";
import { LoadingComponent } from "./core/elements/loading.component";
import { RequestV2Service } from "./core/request-v2.service";
import { UserPreferencesService } from "./core/user-preferences.service";
import {
  PartnersService,
  STORED_SELECTED_ROLE_KEY,
} from "./partner/global/partners.service";

@UntilDestroy()
@Component({
  selector: "mr-root",
  template: `
    @if (isNavigatingChanges | async) {
      <mr-loading></mr-loading>
    }
    @if ((isPartnerChangeInProgressChanges | async) === false) {
      @if (!isInIframe) {
        <router-outlet></router-outlet>
      }
    }
    <mr-alerts></mr-alerts>
  `,
  styles: [
    `
      mr-loading {
        height: 100%;
        align-self: center;
      }
    `,
  ],
  imports: [LoadingComponent, RouterOutlet, AlertsComponent, AsyncPipe],
})
export class RootComponent implements OnInit {
  public constructor(
    private readonly applicationInsights: ApplicationInsightsService,
    private readonly msal: MsalService,
    private readonly msalBroadcast: MsalBroadcastService,
    private readonly request: RequestV2Service,
    private readonly router: Router,
    private readonly partners: PartnersService,
    private readonly user: UserService,
    private readonly userPreferences: UserPreferencesService,
    private readonly userRegistration: UserRegistrationService,
  ) {}

  public readonly isInIframe = BrowserUtils.isInIframe();
  public isPartnerChangeInProgressChanges =
    this.partners.partnerChangingChanges;

  private isLoadingUserAccount = false;

  public readonly isNavigatingChanges = this.router.events.pipe(
    isNavigationStateChange(),
    // Mark as initially navigating in case we missed the "start" event.
    startWith(true),
    // Skip any duplicates in case we did see the "start" event.
    distinctUntilChanged(),
    // Get the start and then the stop. We don't want the whole page to show
    // loading on every navigation, only on the first page load.
    take(2),
    shareReplay(1),
  );

  public ngOnInit(): void {
    // Listen to account changes from other tabs.
    this.msal.instance.enableAccountStorageEvents();

    this.msalBroadcast.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        map(
          () =>
            this.msal.instance.getActiveAccount() ??
            this.msal.instance.getAllAccounts().at(0),
        ),
        // After login, this event will fire but there won't be any accounts
        // stored yet. Instead, we'll handle loading the user details in the
        // `msalSubject$` handler below.
        filter(isExistent),
        switchMap(async (account) => this.loadUserAccount(account)),
        untilDestroyed(this),
      )
      .subscribe();

    this.msalBroadcast.msalSubject$
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        switch (event.eventType) {
          case EventType.ACCOUNT_REMOVED: {
            if (this.msal.instance.getAllAccounts().length === 0) {
              // Logout of all tabs if any tab is logged out in.
              this.user.logout();
            }
            break;
          }
          case EventType.LOGIN_SUCCESS: {
            if (!event.payload.account) {
              throw new Error("Missing account info in login payload.");
            }

            this.loadUserAccount(event.payload.account).catch((error) => {
              this.applicationInsights.logException(error);
              // If something has failed during login, there's not much we can
              // do to recover so just ensure we're fully logged out and the
              // user can try again.
              this.user.logout();
            });
            break;
          }
        }

        // Handle password reset requests.
        // Error message: "The user has forgotten their password."
        if (isAuthErrorWithCode("AADB2C90118", event.error)) {
          this.msal.loginRedirect({
            authority: passwordResetAuthority,
            scopes: ["openid"],
          });
          return;
        }

        // Handle "cancel" click on sign up page.
        // Error message: "The user has cancelled entering self-asserted information."
        if (isAuthErrorWithCode("AADB2C90091", event.error)) {
          this.msal.loginRedirect();
          return;
        }

        // Handle case where user uses old login URL (e.g. from a bookmark) in
        // the middle of a valid login session for some reason.
        // Error message: "No cached authority found."
        // See also the `LoginComponent` for additional handling of stale login
        // page sessions.
        if (isBrowserAuthErrorWithCode("noCachedAuthorityError", event.error)) {
          this.msal.loginRedirect();
          return;
        }
      });

    this.applicationInsights.load();

    // Set a custom logger to track MSAL errors via App Insights
    this.msal.setLogger(
      new Logger({
        logLevel: LogLevel.Info,
        loggerCallback: (logLevel, message) => {
          switch (logLevel) {
            case LogLevel.Error:
              this.applicationInsights.logException(message);
              return;
            case LogLevel.Info:
              this.applicationInsights.logCustomEvent("MSAL Info", {
                message,
              });
              return;
            case LogLevel.Warning:
              this.applicationInsights.logCustomEvent("MSAL Warning", {
                message,
              });
              // eslint-disable-next-line no-console
              console.warn(message);
              return;
          }
        },
      }),
    );

    /*
     * This replaces bootstrapping MsalRedirectComponent.
     *
     * Keep this after the msalSubject$ subscription so LOGIN_SUCCESS and
     * B2C redirect errors are observed by the handlers above.
     */
    this.msal
      .handleRedirectObservable()
      .pipe(untilDestroyed(this))
      .subscribe({
        error: (error) => {
          /*
           * B2C errors are also published through msalSubject$ and handled
           * above. Logging here prevents an unhandled RxJS error.
           */
          this.applicationInsights.logException(error);
        },
      });
  }

  private async loadUserAccount(account: AccountInfo): Promise<void> {
    // Check if already loading and return early
    if (this.isLoadingUserAccount) {
      return;
    }

    this.isLoadingUserAccount = true;
    try {
      this.msal.instance.setActiveAccount(account);

      if (this.user.isValid) {
        return;
      }

      // User should be synced on the server side before fetching the user info,
      // to ensure user's data is up-to-date
      await this.syncUserOnServer();

      const role = await this.loadUserRole();

      const userDetails = await firstExistentValueFrom(
        this.userRegistration.userDetailsChanges,
      );

      this.user.load(account, userDetails, role);

      this.user.loadRegistrationDetails(userDetails);

      await this.user.isLoaded;

      if (!this.user.isValid) {
        if (this.user.isRegistered) {
          await this.router.navigateByUrl("/account-pending");
          return;
        } else {
          await this.router.navigateByUrl("/register");
          return;
        }
      }

      this.applicationInsights.setUserId(this.user.details.id);

      if (this.user.isVendor()) {
        await this.router.navigateByUrl("/vendor-portal");
        return;
      }

      await this.userPreferences.load();
    } finally {
      this.isLoadingUserAccount = false;
    }
  }

  private async syncUserOnServer(): Promise<void> {
    await firstExistentValueFrom(
      this.request.post({
        prefix: GlobalRequestPrefix,
        path: "/users",
        data: { serialize: () => null as never },
      }),
    );
  }

  private async loadUserRole(): Promise<UserRole | null> {
    if (this.user.isPartner()) {
      // Try to get the user role from the session storage first
      const storedUserRole = sessionStorage.getItem(STORED_SELECTED_ROLE_KEY);
      if (storedUserRole) {
        return getEnumMember(UserRole, storedUserRole);
      }
    }
    // If not found, get the first partner's role from the partnerSites endpoint
    const partners = await firstExistentValueFrom(this.partners.changes);

    // If no partners found, then the user could be new user without any role assigned yet
    if (partners.length === 0) {
      return null;
    }
    return partners[0].role;
  }
}

function isAuthErrorWithCode(
  code: string,
  value: EventError,
): value is AuthError {
  return (
    value instanceof AuthError &&
    (value.errorCode === code || value.errorMessage.indexOf(code) > -1)
  );
}

function isBrowserAuthErrorWithCode(
  code: keyof typeof BrowserAuthErrorMessage,
  value: EventError,
): value is BrowserAuthError {
  return (
    value instanceof BrowserAuthError &&
    value.errorCode === BrowserAuthErrorMessage[code].code
  );
}
